import { FullscreenOverlayContainer, OverlayContainer } from "@angular/cdk/overlay";
import { CurrencyPipe, DecimalPipe, PercentPipe } from "@angular/common";
import {
    HTTP_INTERCEPTORS,
    HttpClientModule,
    provideHttpClient,
    withInterceptorsFromDi,
} from "@angular/common/http";
import { ErrorHandler, Injector, NgModule, inject, provideAppInitializer } from "@angular/core";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { BrowserModule } from "@angular/platform-browser";
import {
    RouteReuseStrategy,
    RouterModule,
    Routes,
    UrlSerializer,
    Router,
} from "@angular/router";
import { CerrixRouteReuseStrategy } from "./cerrix-route-reuse-strategy";
import * as Sentry from "@sentry/angular";
import { ThirdpartyModule } from "@pages/thirdparty/thirdparty.module";
import { AccordionModule } from "ngx-bootstrap/accordion";
import { BsDatepickerConfig, BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { ModalModule } from "ngx-bootstrap/modal";
import { PopoverModule } from "ngx-bootstrap/popover";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { SortablejsModule } from "./shared/sortablejs/sortablejs.module";
import { ToastrModule } from "ngx-toastr";
import { PageLookup } from "../common/constants/pages/page-lookup";
import { Pages } from "../common/constants/pages/Pages";
import { StandingDataMapping } from "../common/constants/standingdatamapping";
import { IsAuthenticated } from "../common/guards/IsAuthenticated";
import { HasRiskAccess } from "../common/guards/risk/HasRiskAccess";
import { ByteArray } from "../common/pipes/utils/ByteArray";
import { EnumKeyValuePair } from "../common/pipes/utils/EnumKeyValuePair";
import { TabComponent } from "../core-components/tab/tab.component";
import { TabSystemDirective } from "../directives/tab-system.directive";
import { InviteComponent } from "../pages/invite/invite.component";
import { DocumentDataService } from "../services/http/document/DocumentDataService";
import { JsonDateHttpInterceptor } from "../services/http/interceptors/json-date-http-interceptor";
import { ResponseHttpInterceptor } from "../services/http/interceptors/response-http-interceptor";
import { InviteDataService } from "../services/http/invite/InviteDataService";
import { PermissionsDataService } from "../services/http/permissions/PermissionsDataService";
import { SettingsDataService } from "../services/http/SettingsDataService";
import { PermissionsService } from "../services/permissions/PermissionsService";
import { TabService } from "../services/tabs/TabService";
import { LowerCaseUrlSerializer } from "../services/URLSerializer";
import { TabMenuItemChildSelectedPipe } from "./../common/pipes/utils/tab-menu.pipe";
import { AboutComponent } from "./about/about.component";
import { AdminModule } from "./admin/admin.module";
import { ApiKeyModule } from "./user/shared/api-key/api-key.module";
import { AppComponent } from "./app.component";
import { AppConstants, Configuration } from "./app.constants";
import { AssessmentModule } from "./assessment/assessment.module";
import { AuditFindingModule } from "./audit-finding/audit-finding.module";
import { AuditUniversePeriodModule } from "./audit-universe-period/audit-universe-period.module";
import { AuditUniverseModule } from "./audit-universe/audit-universe.module";
import { AuditModule } from "./audit/audit.module";
import { AuthenticationModule } from "./authentication/authentication.module";
import { LoginComponent } from "./authentication/login/login.component";
import { CerrixCalendarModule } from "./calendar/calendar.module";
import { CerrixAppComponent } from "./cerrix-app/cerrix-app.component";
import { ControlModule } from "./controls/control.module";
import { AdvancedTestingModule } from "./controls/testing/advanced/advanced-testing.module";
import { SimpleTestingModule } from "./controls/testing/simple/simple-testing.module";
import { DashboardModule } from "./dashboard/dashboard.module";
import { DatamanagementModule } from "./datamanagement/datamanagement.module";
import { DeletedItemsModule } from "./deleted-items/deleted-items.module";
import { AdminDocumentsComponent } from "./documents/admin/admin-documents.component";
import { SharedDocumentsComponent } from "./documents/shared/shared-documents.component";
import { BulkDeletedDocumentsComponent } from "./documents/bulk-deleted/bulk-deleted-documents.component";
import { EventModule } from "./event/event.module";
import { EventDataService } from "./event/services/EventDataService";
import { ExportOverviewComponent } from "./export/export-overview.component";
import { ExportModule } from "./export/export.module";
import {
    ExportBimComponent,
    ExportControlsComponent,
    ExportDatamanagementComponent,
    ExportEventsComponent,
    ExportRisksComponent,
    ExportTasksComponent,
    ExportIncidentsComponent,
} from "./export/exporttypes";
import { ExternalConnectionModule } from "./external-connections/external-connection.module";
import { FindingReportModule } from "./findingreport/finding-report.module";
import { CerrixFormsModule } from "./forms/forms.module";
import { KriModule } from "./kri/kri.module";
import { MailMessagesModule } from "./mailmessages/mailmessages.module";
import { ManualModule } from "./manual/manual.module";
import { MoiModule } from "./moi/moi.module";
import { NavbarModule } from "./navbar/navbar.module";
import { BusinessNavigatorModule } from "./navigators/business-navigator/business-navigator.module";
import { PeriodsModule } from "./periods/periods.module";
import { ProcessEditorModule } from "./process-editor/process-editor.module";
import { ReportModule } from "./report/report.module";
import { RiskModule } from "./risk/risk.module";
import { RiskDataService } from "./risk/services/RiskDataService";
import { SettingsComponent } from "./settings/settings.component";
import { TranslocoService } from "@jsverse/transloco";
import { isPseudoEnabled, readCachedLanguage } from "./lang-override";
import { ALL_LANG_CODES, languageToLangCode, resolveEffectiveLanguage } from "./resolve-effective-language";
import { AppConfigService } from "./shared/services/app-config.service";
import { ClientConfigService } from "./shared/services/client-config.service";
import { ErrorHandlerService } from "./shared/services/error-handler.service";
import { MonitoringService } from "./shared/services/monitoring.service";
import { ObservabilityService } from "./shared/services/observability.service";
import { I18nObservabilityService } from "./shared/services/i18n-observability.service";
import { SharedModule } from "./shared/shared.module";
import { GenericListManagerComponent } from "./shared/generic-managers/generic-list-manager/generic-list-manager.component";
import { GENERIC_LIST_MANAGER } from "./shared/generic-managers/generic-list-manager/generic-list-manager.token";
import { StandingdataModule } from "./standingdata/standingdata.module";
import { TaskModule } from "./tasks/task.module";
import { WopiModule } from "./wopi/wopi.module";
import { UserMaintenanceModule } from "./user/user-maintenance/user-maintenance.module";
import { UserProfileModule } from "./user/user-profile/user-profile.module";
import { ScheduleModule } from "@syncfusion/ej2-angular-schedule";
import { DocumentTemplatesModule } from "./document-templates/document-templates.module";
import { JobSchedulerModule } from "./job-scheduler/job-scheduler.module";
import { ContextMenuService } from "@syncfusion/ej2-angular-grids";
import { DocumentIntelligenceModule } from "./document-intelligence/document-intelligence.module";
import { OrganizationBudgetComponent } from "./org-budget/organization-budget.component";
import { OrganizationBudgetDataService } from "./org-budget/organization-budget-data.service";
import { AiModule } from "./ai/ai.module";
import { PresetService } from "./preset/services/preset.service";
import { WorkdayOrganizerModule } from "./workday-organizer/workday-organizer-module";
import { AssetManagementModule } from "./assetmanagement/assetmanagement.module";

const routes: Routes = [
    { path: "login", component: LoginComponent },
    {
        path: "",
        component: CerrixAppComponent,
        pathMatch: "full",
        canLoad: [IsAuthenticated],
        canActivate: [IsAuthenticated],
    },
    {
        path: "**",
        component: CerrixAppComponent,
        canLoad: [IsAuthenticated],
        canActivate: [IsAuthenticated],
    },
];

export function initializeAppConfig(
    appConfigService: AppConfigService,
    monitoringService: MonitoringService,
) {
    return () =>
        appConfigService.initialize().then((config) => {
            // Must initialize here because ErrorHandlers are loaded before APP_INITIALIZER
            monitoringService.initialize(config.appInsightsInstrumentationKey);
        });
}

export function initializeClientConfig(
    clientConfigService: ClientConfigService,
    translocoService: TranslocoService,
) {
    return () =>
        clientConfigService
            .initialize()
            .then((config) => {
                // Transloco decides whether a string is a plain lang or a scope path by checking
                // this list, so every real lang must be registered here or setActiveLang("nl"/"no")
                // gets misread as an (empty) scope name and fails to load. The frontend is always
                // built in prod mode, so environment.production is true on every deployed box; the
                // backend i18nToolingEnabled flag is the only runtime signal that distinguishes a
                // Development environment (e.g. dev.cerrix.com) from real production, and adds
                // "pseudo" so the navbar's checkbox (or window.cerrix.setLang) can switch into it.
                translocoService.setAvailableLangs(
                    config.i18nToolingEnabled ? [...ALL_LANG_CODES, "pseudo"] : ALL_LANG_CODES
                );

                if (config.i18nToolingEnabled && isPseudoEnabled()) {
                    translocoService.setActiveLang("pseudo");
                    return;
                }

                // No user is known yet at this point (pre-login), so use the last language
                // EffectiveLanguageService resolved for this browser as a best guess (a
                // returning user sees their own language immediately instead of flashing to the
                // tenant default). Re-resolved for real once authenticated, see
                // CerrixAppComponent - the cache is never authoritative on its own.
                const cached = readCachedLanguage();
                const langCode =
                    cached ?? languageToLangCode(resolveEffectiveLanguage(null, config.defaultLanguage));
                translocoService.setActiveLang(langCode);
            })
            // Fail open. This flag only drives dev tooling, so a failed call must never stop the
            // app from bootstrapping: a rejected APP_INITIALIZER leaves the user on a blank page
            // instead of the login screen. The call can fail on a real environment, e.g. a stale
            // or foreign session cookie makes the tenant gate answer 401. Swallowing the error
            // keeps the English-only baseline from shared.module.ts.
            .catch(() => {});
}

export function getDatepickerConfig(): BsDatepickerConfig {
    return Object.assign(new BsDatepickerConfig(), {
        dateInputFormat: "DD-MM-YYYY",
        rangeInputFormat: "DD-MM-YYYY",
        containerClass: "theme-default",
        customTodayClass: "datepicker-today",
    });
}

@NgModule({
    declarations: [
        // Pipes
        ByteArray,
        EnumKeyValuePair,
    ],
    exports: [
        // Services
        HttpClientModule,
        RouterModule,
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        ScheduleModule,
        AppComponent,
        TabSystemDirective,
        TabComponent,
        TabMenuItemChildSelectedPipe,
        CerrixAppComponent,
        AboutComponent,
        SharedDocumentsComponent,
        ExportOverviewComponent,
        ExportRisksComponent,
        ExportEventsComponent,
        ExportBimComponent,
        ExportControlsComponent,
        ExportDatamanagementComponent,
        ExportTasksComponent,
        ExportIncidentsComponent,
        SettingsComponent,
        InviteComponent,
        OrganizationBudgetComponent,
        AdminDocumentsComponent,
        BulkDeletedDocumentsComponent,
        // Modules
        SharedModule.forRoot(),
        NavbarModule.forRoot(),
        MoiModule.forRoot(),
        KriModule.forRoot(),
        AdvancedTestingModule.forRoot(),
        EventModule.forRoot(),
        FindingReportModule,
        AssessmentModule,
        CerrixFormsModule.forRoot(),
        CerrixCalendarModule.forRoot(),
        ControlModule.forRoot(),
        SimpleTestingModule.forRoot(),
        AdminModule.forRoot(),
        JobSchedulerModule.forRoot(),
        WorkdayOrganizerModule.forRoot(),
        RiskModule.forRoot(),
        DatamanagementModule.forRoot(),
        BusinessNavigatorModule.forRoot(),
        UserMaintenanceModule.forRoot(),
        ProcessEditorModule.forRoot(),
        AuthenticationModule.forRoot(),
        UserProfileModule.forRoot(),
        ThirdpartyModule.forRoot(),
        ExportModule,
        StandingdataModule.forRoot(),
        DeletedItemsModule.forRoot(),
        PeriodsModule.forRoot(),
        MailMessagesModule.forRoot(),
        ApiKeyModule.forRoot(),
        DashboardModule.forRoot(),
        ReportModule.forRoot(),
        ManualModule.forRoot(),
        AuditUniverseModule.forRoot(),
        AuditUniversePeriodModule.forRoot(),
        AuditModule.forRoot(),
        AuditFindingModule.forRoot(),
        ExternalConnectionModule.forRoot(),
        TaskModule.forRoot(),
        WopiModule.forRoot(),
        DocumentTemplatesModule,
        DocumentIntelligenceModule.forRoot(),
        AiModule.forRoot(),
        AssetManagementModule.forRoot(),

        RouterModule,
        // Routing
        // Routing
        RouterModule.forRoot(routes),
        // Libraries
        ToastrModule.forRoot({
            progressBar: true,
            progressAnimation: "increasing",
            positionClass: "toast-bottom-right",
        }),
        AccordionModule.forRoot(),
        BsDropdownModule.forRoot(),
        PopoverModule.forRoot(),
        ModalModule.forRoot(),
        TooltipModule.forRoot(),
        SortablejsModule.forRoot({ animation: 150 }),
        BsDatepickerModule.forRoot(),
    ],
    providers: [
        Configuration,
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
        { provide: UrlSerializer, useClass: LowerCaseUrlSerializer },
        { provide: RouteReuseStrategy, useClass: CerrixRouteReuseStrategy },
        TabService,
        SettingsDataService,
        PermissionsService,
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        { provide: Sentry.TraceService, deps: [Router] },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeAppConfig(
                inject(AppConfigService),
                inject(MonitoringService),
            );
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeClientConfig(
                inject(ClientConfigService),
                inject(TranslocoService),
            );
            return initializerFn();
        }),
        provideAppInitializer(() => {
            return inject(ObservabilityService).initGlobal();
        }),
        provideAppInitializer(() => {
            return inject(I18nObservabilityService).startMonitoring();
        }),
        { provide: HTTP_INTERCEPTORS, useClass: ResponseHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: JsonDateHttpInterceptor, multi: true },
        { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
        { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
        RiskDataService,
        EventDataService,
        PermissionsDataService,
        PresetService,
        DocumentDataService,
        InviteDataService,
        OrganizationBudgetDataService,
        // The custom field types of generic-field-editor render a nested list manager through
        // this token instead of a static import, which keeps their import graph acyclic. The
        // compiler does not check this wiring. Without this provider they fail at runtime with
        // a NullInjectorError.
        { provide: GENERIC_LIST_MANAGER, useValue: GenericListManagerComponent },
        // Constants
        Pages,
        PageLookup,
        StandingDataMapping,
        // Pipes
        ByteArray,
        EnumKeyValuePair,
        DecimalPipe,
        CurrencyPipe,
        PercentPipe,
        TabMenuItemChildSelectedPipe,
        // Guards
        IsAuthenticated,
        HasRiskAccess,
        // Syncfusion
        ContextMenuService,
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {
    constructor() {
        const injector = inject(Injector);

        AppConstants.AppInjector = injector;
        AppConstants.DefaultIcon = "fas fa-building";
    }
}
